import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ScrollErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Actualizar el estado para mostrar la UI de error
    return { hasError: true, error };
  }

  componentDidMount() {
    // Clear the reload flag on successful mount so future errors can still reload
    sessionStorage.removeItem("chunk_error_reload_attempted");
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log del error para debugging
    console.error("ScrollErrorBoundary caught an error:", error, errorInfo);

    // Llamar callback personalizado si existe
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Auto-reload for chunk loading errors (stale deployment / new hash after deploy)
    const isChunkError =
      error.message.includes("dynamically imported module") ||
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.message.includes("error loading dynamically imported module") ||
      // Firefox-specific: stale chunk returns HTML (index.html rewrite) instead of JS
      (error instanceof SyntaxError && error.message.includes("got '<'")) ||
      (error instanceof SyntaxError && error.message.includes("unexpected token '<'")) ||
      (error instanceof SyntaxError && error.message.includes("Unexpected token '<'"));

    if (isChunkError) {
      const reloadKey = "chunk_error_reload_attempted";
      const alreadyReloaded = sessionStorage.getItem(reloadKey);
      if (!alreadyReloaded) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
        return;
      }
    }

    // Intentar recuperación automática para errores de scroll
    if (
      error.message.includes("scroll") ||
      error.message.includes("animation") ||
      error.message.includes("transform")
    ) {
      

      // Limpiar transformaciones problemáticas
      try {
        document.body.style.transform = "";
        document.body.style.willChange = "auto";

        // Forzar repaint
        document.body.offsetHeight;

        // Intentar recuperación después de un breve delay
        setTimeout(() => {
          this.setState({ hasError: false, error: undefined });
        }, 1000);
      } catch (recoveryError) {
        console.error("Error during recovery:", recoveryError);
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // UI de fallback personalizada
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI de fallback por defecto
      return (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              ooops something went wrong
            </h2>
            <p className="text-gray-600 mb-6">
              We are experiencing a technical issue. The page will automatically
              reload.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ScrollErrorBoundary;
